import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import CasinoChip from "../pages/SpinAndWin/CasinoChip";
import Axios from "../Axios";
import borderBg from "../assets/border.png";
import toast from "react-hot-toast";
import { playMultiplierSound, playSound } from "./Soundmaker";
import RewardValueBadge from "./RewardValueBadge";
import { getRewardStyle } from "../utils/rewardStyles";
import { use3DTilt } from "./phase1/use3DTilt";
import { startPhase1SpinAudio, stopPhase1SpinAudio } from "./phase1/phase1Audio";
import spinWheelChassisImg from "../assets/spin_wheel_chassis.png";
import spinWheelCenterBtnImg from "../assets/spin_wheel_center_btn.png";
import "./phase1/phase1.css";

const WHEEL_PRESETS = {
  spin: {
    title: "Spin Wheel",
    spinLabel: "Spin",
    segments: [
      { label: "$30 Credit", value: "30", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "freeCredit" },
      { label: "$40 Credit", value: "40", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "freeCredit" },
      { label: "$50 Credit", value: "50", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "freeCredit" },
      { label: "$1 Cash", value: "1", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "cash" },
      { label: "$2 Cash", value: "2", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "cash" },
      { label: "$5 Cash", value: "5", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "cash" },
      { label: "$10 Cash", value: "10", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "cash" },
      { label: "Nothing", value: "0", isFreeSpin: false, isVisible: true, isWin: false, rewardType: "none" },
      { label: "Jackpot Ticket", value: "1", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "jackpotTicket" },
      { label: "Jackpot Ticket", value: "1", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "jackpotTicket" },
      { label: "1.25x Multiplier", value: "1.25", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "multiplier" },
      { label: "1.5x Multiplier", value: "1.5", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "multiplier" },
      { label: "2x Multiplier", value: "2", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "multiplier" },
      { label: "2.5x Multiplier", value: "2.5", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "multiplier" },
      { label: "3x Multiplier", value: "3", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "multiplier" },
      { label: "$1 Credit", value: "1", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "freeCredit" },
      { label: "$2 Credit", value: "2", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "freeCredit" },
      { label: "$5 Credit", value: "5", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "freeCredit" },
      { label: "$10 Credit", value: "10", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "freeCredit" },
      { label: "$20 Credit", value: "20", isFreeSpin: false, isVisible: true, isWin: true, rewardType: "freeCredit" },
    ],
  },
  jackpot: {
    title: "Jackpot Wheel",
    spinLabel: "Spin Jackpot",
    segments: [
      { label: "Jackpot Ticket x2", color: "#f6d56c" },
      { label: "3%", color: "#7b120a" },
      { label: "5%", color: "#f6d56c" },
      { label: "10%", color: "#97180f" },
      { label: "20%", color: "#f6d56c" },
      { label: "50%", color: "#9e150d" },
      { label: "Nothing", color: "#5f100b" },
      { label: "100%", color: "#f6d56c" },
    ],
  },
};

const AUTO_SEGMENT_COLORS = [
  "#ef4444",
  "#22c55e",
  "#3b82f6",
  "#eab308",
  "#a855f7",
  "#f97316",
  "#ec4899",
  "#14b8a6",
  "#f59e0b",
  "#6366f1",
  "#84cc16",
  "#06b6d4",
];

const ACCEL_DURATION_MS = 900;
const MAX_SPEED_DEG_PER_MS = 0.72;
const MIN_FULL_ROTATIONS_BEFORE_DECEL = 5;

// Physics-based ease: constant deceleration (velocity linear drop from v0 to 0).
// Derivative at t=0 is exactly 2, so setting T = 2*D/v0 makes the wheel
// seamlessly continue at its cruise speed when decel begins — no lurch.
function easeOutQuad(t) {
  return 1 - (1 - t) * (1 - t);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeAngle(angle) {
  return ((angle % 360) + 360) % 360;
}

// Jackpot wheel alternating colors — red and golden
const JACKPOT_COLOR_RED = "#7b120a";
const JACKPOT_COLOR_GOLD = "#f6d56c";

function getWheelSegmentColor(segment, presetSegments, index, isJackpot = false) {
  if (isJackpot) {
    return index % 2 === 0 ? JACKPOT_COLOR_RED : JACKPOT_COLOR_GOLD;
  }
  return (
    segment.color ||
    presetSegments[index]?.color ||
    AUTO_SEGMENT_COLORS[index % AUTO_SEGMENT_COLORS.length]
  );
}

function calculateWinningRotation(currentRotation, stopIndex, segmentAngle, extraRotations, offsetFraction = 0.5) {
  const segmentTarget = stopIndex * segmentAngle + segmentAngle * offsetFraction;
  const targetRotation = normalizeAngle(360 - segmentTarget);
  const normalizedCurrent = normalizeAngle(currentRotation);
  const delta = (targetRotation - normalizedCurrent + 360) % 360;
  return currentRotation + Math.ceil(extraRotations) * 360 + delta;
}

function getRewardIcon(label, rewardType) {
  const text = String(label || "").toLowerCase();
  const type = String(rewardType || "").toLowerCase();
  if (type === "multiplier" || text.includes("multiplier")) return "⚡";
  if (type === "freecredit" || text.includes("credit")) return "🪙";
  if (type === "cash" || text.includes("cash")) return "💵";
  if (type === "jackpotticket" || text.includes("ticket") || text.includes("jackpot")) return "🎟️";
  if (type === "none" || text.includes("nothing")) return "✖";
  return "⭐";
}

function formatWheelLabel(label) {
  const normalizedLabel = String(label).trim().replace(/\s+/g, " ");
  if (!normalizedLabel) return ["--"];

  const words = normalizedLabel.split(" ");
  if (words.length === 1) return [normalizedLabel];

  if (words.length === 2 && normalizedLabel.length > 6) {
    return [words[0], words[1]];
  }

  if (words.length <= 2) return [normalizedLabel];

  const midpoint = Math.ceil(words.length / 2);
  return [words.slice(0, midpoint).join(" "), words.slice(midpoint).join(" ")];
}

function getSectorPath(startAngle, endAngle, radius = 200) {
  const rad1 = ((startAngle - 90) * Math.PI) / 180;
  const rad2 = ((endAngle - 90) * Math.PI) / 180;
  const x1 = radius * Math.cos(rad1);
  const y1 = radius * Math.sin(rad1);
  const x2 = radius * Math.cos(rad2);
  const y2 = radius * Math.sin(rad2);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M 0 0 L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${radius} ${radius} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
}

function parseWheelLabel(label) {
  const norm = String(label || "").trim().replace(/\s+/g, " ");
  if (!norm) return { valueText: "--", subText: "" };

  const mMoney = norm.match(/^(\$\d+)\s*(Credit|Cash)$/i);
  if (mMoney) return { valueText: mMoney[1], subText: mMoney[2] };

  const mMult = norm.match(/^(\d+(?:\.\d+)?x)\s*(Multiplier)$/i);
  if (mMult) return { valueText: mMult[1], subText: mMult[2] };

  if (/^Jackpot\s*Ticket$/i.test(norm)) {
    return { valueText: "Jackpot", subText: "Ticket" };
  }
  if (/^Nothing$/i.test(norm)) {
    return { valueText: "Nothing", subText: "" };
  }
  const parts = norm.split(" ");
  return { valueText: parts[0], subText: parts.slice(1).join(" ") };
}

export function TradeWheelModal({
  open,
  variant = "spin",
  segments: propSegments = [],
  onClose,
  onSpinSuccess,
  onMultiplierReward,
  jackpotTickets,
  poolAmount,
  hasSpin,
}) {
  const autoCloseAfterSpin = variant === "spin";
  const isJackpotVariant = variant === "jackpot";
  const config = WHEEL_PRESETS[variant] ?? WHEEL_PRESETS.spin;
  const segments = (propSegments && propSegments.length > 0)
    ? propSegments
    : (WHEEL_PRESETS[variant]?.segments || []);

  const segmentCount = segments.length;
  const segmentAngle = segmentCount > 0 ? 360 / segmentCount : 0;
  const labelRadiusPercent = isJackpotVariant ? 34 : 35;

  // UI state
  const [resultLabel, setResultLabel] = useState("");
  const [isSpinning, setIsSpinning] = useState(false);
  const [isResultProcessing, setIsResultProcessing] = useState(false);
  const [canSpinAgain, setCanSpinAgain] = useState(true);
  const [lastResultWasFree, setLastResultWasFree] = useState(false);
  const [winningIndex, setWinningIndex] = useState(null);
  const [showWinCelebration, setShowWinCelebration] = useState(false);
  const INITIAL_WHEEL_ROTATION = 351;
  const [restRotation, setRestRotation] = useState(INITIAL_WHEEL_ROTATION);

  // Refs - control animation without triggering re-renders
  const wheelRef = useRef(null);
  const rafRef = useRef(null);
  const spinTimeoutRef = useRef(null);
  const currentRotationRef = useRef(351);
  const isSpinningRef = useRef(false);
  const segmentAngleRef = useRef(segmentAngle);
  const segmentsRef = useRef(segments);
  const variantRef = useRef(variant);
  const pendingStopIndexRef = useRef(null);
  const autoCloseRef = useRef(autoCloseAfterSpin);
  const animateFrameRef = useRef(null);

  segmentAngleRef.current = segmentAngle;
  segmentsRef.current = segments;
  variantRef.current = variant;
  autoCloseRef.current = autoCloseAfterSpin;

  const animationState = useRef({
    phase: "idle",
    startRotation: 0,
    startTime: 0,
    lastFrameTime: 0,
    decelStartRotation: 0,
    decelStartTime: 0,
    decelDuration: 0,
    finalRotation: 0,
    decelOvershoot: 1.5,
    finalized: false,
  });

  const onCloseRef = useRef(onClose);
  const onSpinSuccessRef = useRef(onSpinSuccess);
  const onMultiplierRewardRef = useRef(onMultiplierReward);
  const hasSpinRef = useRef(hasSpin);
  onCloseRef.current = onClose;
  onSpinSuccessRef.current = onSpinSuccess;
  onMultiplierRewardRef.current = onMultiplierReward;
  hasSpinRef.current = hasSpin;

  const wheelBackground = useMemo(() => {
    if (segmentCount === 0) return "#2a2a2a";
    const presetSegments = WHEEL_PRESETS[variant]?.segments || [];
    const stops = segments
      .map((segment, index) => {
        const start = index * segmentAngle;
        const end = (index + 1) * segmentAngle;
        const baseColor = getWheelSegmentColor(segment, presetSegments, index, isJackpotVariant);
        if (!isJackpotVariant) {
          return `color-mix(in srgb, black 35%, ${baseColor}) ${start}deg, ${baseColor} ${start + segmentAngle * 0.45}deg, color-mix(in srgb, white 15%, ${baseColor}) ${end}deg`;
        }
        return `${baseColor} ${start}deg ${end}deg`;
      })
      .join(",");
    return `conic-gradient(${stops})`;
  }, [segments, segmentAngle, segmentCount, variant, isJackpotVariant]);

  useEffect(() => {
    if (open) {
      setCanSpinAgain(true);
      setResultLabel("");
      setWinningIndex(null);
      setShowWinCelebration(false);
      setRestRotation(currentRotationRef.current);
      if (wheelRef.current) {
        wheelRef.current.style.transform = `rotate(${currentRotationRef.current}deg)`;
      }
    }
  }, [open, variant]);

  useEffect(() => {
    const handler = (e) => {
      if (isSpinningRef.current) {
        e.preventDefault();
        e.returnValue = "Are you sure you want to reload?";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  useEffect(() => {
    return () => {
      isSpinningRef.current = false;
      animationState.current.phase = "idle";
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
    };
  }, []);

  const setWheelAngle = useCallback((angle) => {
    currentRotationRef.current = angle;
    if (wheelRef.current) {
      wheelRef.current.style.transform = `rotate(${angle}deg)`;
    }
  }, []);

  const finalizeSpinRef = useRef(null);

  const beginDecelerationRef = useRef(null);
  beginDecelerationRef.current = (time) => {
    const state = animationState.current;
    const stopIndex = pendingStopIndexRef.current;
    if (stopIndex == null) return false;
    state.phase = "decelerating";
    state.decelStartRotation = currentRotationRef.current;
    state.decelStartTime = time;
    const offsetFraction = 0.5;
    const extraRotations = 3 + Math.floor(Math.random() * 2);
    state.finalRotation = calculateWinningRotation(
      currentRotationRef.current,
      stopIndex,
      segmentAngleRef.current,
      extraRotations,
      offsetFraction,
    );
    const distance = state.finalRotation - state.decelStartRotation;
    state.decelDuration = Math.max(2200, (2 * distance) / MAX_SPEED_DEG_PER_MS);
    return true;
  };

  finalizeSpinRef.current = async () => {
    const state = animationState.current;
    if (state.finalized) return;
    state.finalized = true;
    state.phase = "idle";

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
      spinTimeoutRef.current = null;
    }

    stopPhase1SpinAudio();

    const stopIndex = pendingStopIndexRef.current ?? 0;
    const result = segmentsRef.current[stopIndex];
    const reward = result?.label || "";
    const finalAngle = state.finalRotation || currentRotationRef.current;

    setWheelAngle(finalAngle);
    setRestRotation(finalAngle);
    setResultLabel(reward);
    setWinningIndex(stopIndex);
    setShowWinCelebration(true);
    setIsSpinning(false);
    isSpinningRef.current = false;
    setIsResultProcessing(true);

    const isFreeSpin = String(reward).toUpperCase().includes("SPIN");
    try {
      if (onSpinSuccessRef.current) await onSpinSuccessRef.current();

      if (variantRef.current === "spin") {
        setCanSpinAgain(isFreeSpin || (hasSpinRef.current && hasSpinRef.current.spinCount > 0));
      }
      setLastResultWasFree(isFreeSpin);

      const rewardText = String(reward || "");
      const rewardType = String(result?.rewardType || "").toLowerCase();
      const isMultiplier =
        rewardType === "multiplier" ||
        /multiplier/i.test(rewardText) ||
        /\d+(?:\.\d+)?\s*x\b/i.test(rewardText) ||
        /\bx\s*\d+(?:\.\d+)?/i.test(rewardText);

      if (isMultiplier) {
        setTimeout(() => {
          if (onCloseRef.current) onCloseRef.current();
          if (onMultiplierRewardRef.current) onMultiplierRewardRef.current(reward);
        }, 2200);
      } else if (isFreeSpin) {
        toast.success("FREE SPIN! Spin again!", {
          icon: "🎁",
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
      } else if (autoCloseRef.current && onCloseRef.current) {
        setTimeout(() => {
          if (!hasSpinRef.current || hasSpinRef.current.spinCount <= 0) {
            if (onCloseRef.current) onCloseRef.current();
          }
        }, 2400);
      }
    } catch (error) {
      console.error(error);
      toast.error("Result could not be processed.");
    } finally {
      setIsResultProcessing(false);
      pendingStopIndexRef.current = null;
      state.finalized = false;
    }
  };

  animateFrameRef.current = (time) => {
    const state = animationState.current;

    if (!isSpinningRef.current || state.phase === "idle") {
      return;
    }

    if (!state.lastFrameTime) {
      state.lastFrameTime = time;
      state.startTime = time;
    }

    const dt = time - state.lastFrameTime;
    state.lastFrameTime = time;

    if (state.phase === "accelerating") {
      const elapsed = time - state.startTime;
      const t = Math.min(elapsed / ACCEL_DURATION_MS, 1);
      const speedFactor = t * t;
      const frameSpeed = MAX_SPEED_DEG_PER_MS * speedFactor;

      setWheelAngle(currentRotationRef.current + frameSpeed * dt);

      const rotationsCompleted =
        (currentRotationRef.current - state.startRotation) / 360;

      if (
        pendingStopIndexRef.current != null &&
        rotationsCompleted >= MIN_FULL_ROTATIONS_BEFORE_DECEL
      ) {
        beginDecelerationRef.current(time);
      }
    }

    if (state.phase === "decelerating") {
      const progress = clamp(
        (time - state.decelStartTime) / state.decelDuration,
        0,
        1,
      );
      const angle =
        state.decelStartRotation +
        (state.finalRotation - state.decelStartRotation) * easeOutQuad(progress);

      setWheelAngle(angle);

      if (progress >= 1) {
        void finalizeSpinRef.current();
        return;
      }
    }

    rafRef.current = requestAnimationFrame((t) => animateFrameRef.current(t));
  };

  const isSpinVariant = variant === "spin";
  const currentHubDisabled =
    (isSpinVariant && !hasSpin?.success && !lastResultWasFree) ||
    (isJackpotVariant && jackpotTickets <= 0);

  const handleSpin = async () => {
    if (
      isSpinningRef.current ||
      isResultProcessing ||
      segmentCount === 0 ||
      currentHubDisabled ||
      !canSpinAgain
    ) return;

    if (variant === "jackpot" && jackpotTickets <= 0) {
      toast.error("No Jackpot Tickets");
      return;
    }

    isSpinningRef.current = true;
    setIsSpinning(true);
    setResultLabel("");
    setWinningIndex(null);
    setShowWinCelebration(false);
    setLastResultWasFree(false);
    setIsResultProcessing(false);

    pendingStopIndexRef.current = null;
    const state = animationState.current;
    state.phase = "accelerating";
    state.startRotation = currentRotationRef.current;
    state.startTime = 0;
    state.lastFrameTime = 0;
    state.decelStartRotation = 0;
    state.decelStartTime = 0;
    state.decelDuration = 0;
    state.finalRotation = 0;
    state.decelOvershoot = 1.5;
    state.finalized = false;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (wheelRef.current) {
      wheelRef.current.style.transition = "none";
    }

    rafRef.current = requestAnimationFrame((t) => animateFrameRef.current(t));
    if (!isJackpotVariant) {
      startPhase1SpinAudio();
    }

    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
    }
    spinTimeoutRef.current = setTimeout(() => {
      if (isSpinningRef.current && pendingStopIndexRef.current === null) {
        console.warn("[TradeWheel] Spin timeout reached");
        if (!isJackpotVariant) stopPhase1SpinAudio({ immediate: true });
        toast.error("Connection timed out. Please try again.");
        isSpinningRef.current = false;
        setIsSpinning(false);
        setIsResultProcessing(false);
        animationState.current.phase = "idle";
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      }
    }, 12000);

    try {
      const { data } = await Axios.post("/spinWheel/spin", { variant });
      const stopIndex = data?.result?.stopIndex ?? 0;
      pendingStopIndexRef.current = stopIndex;
    } catch (error) {
      console.error(error);
      if (!isJackpotVariant) stopPhase1SpinAudio({ immediate: true });
      isSpinningRef.current = false;
      setIsSpinning(false);
      setIsResultProcessing(false);
      animationState.current.phase = "idle";
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
        spinTimeoutRef.current = null;
      }
      toast.error("Spin failed. Please try again.");
    }
  };

  const { sceneRef, cardRef, tiltHandlers } = use3DTilt({ maxTilt: 12, perspective: 900 });

  useEffect(() => {
    if (!open && !isJackpotVariant) stopPhase1SpinAudio({ immediate: true });
    return () => {
      if (!isJackpotVariant) stopPhase1SpinAudio({ immediate: true });
    };
  }, [open, isJackpotVariant]);

  if (!open) return null;

  const isBusy = isSpinning || isResultProcessing;
  const spinDisabled = isBusy || !canSpinAgain;

  // ===== UPDATED 3D ARCADE SPIN WHEEL (matches new.mp4) =====
  if (!isJackpotVariant) {
    const remainingSpins = hasSpin && hasSpin.spinCount !== undefined ? hasSpin.spinCount : 0;
    return (
      <div
        className="trade-wheel-arcade"
        role="presentation"
        ref={sceneRef}
        {...tiltHandlers}
        onClick={() => {
          if (!isBusy && onClose) onClose();
        }}
      >
        <style>{`
          .trade-wheel-arcade {
            position: fixed;
            inset: 0;
            z-index: 10050;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 0, 0, 0.65);
            backdrop-filter: none;
            -webkit-backdrop-filter: none;
            overflow: hidden;
            touch-action: none;
          }
          @keyframes arcadeWheelEntrance {
            0% { transform: scale(0.8) translateY(30px); opacity: 0; }
            70% { transform: scale(1.02) translateY(-2px); opacity: 1; }
            100% { transform: scale(1) translateY(0); opacity: 1; }
          }
          @keyframes arrowMechanicalTick {
            0% { transform: translateX(-50%) rotate(-3.5deg); }
            100% { transform: translateX(-50%) rotate(3.5deg); }
          }
          @keyframes arcadeWinningSlicePulse {
            0% { opacity: 0.82; filter: drop-shadow(0 0 10px #ffd700); }
            100% { opacity: 1; filter: drop-shadow(0 0 22px #ffd700) drop-shadow(0 0 35px #ff8800); }
          }
          @keyframes laserBeamPulse {
            0% { opacity: 0.8; height: 68px; filter: drop-shadow(0 0 6px #ffd700); }
            100% { opacity: 1; height: 78px; filter: drop-shadow(0 0 14px #ffd700) drop-shadow(0 0 25px #ff9900); }
          }
          @keyframes arcadeWinBannerPop {
            0% { transform: translateX(-50%) scale(0.6); opacity: 0; }
            70% { transform: translateX(-50%) scale(1.08); opacity: 1; }
            100% { transform: translateX(-50%) scale(1); opacity: 1; }
          }
          .arcade-laser-beam {
            animation: laserBeamPulse 1s ease-in-out infinite alternate;
          }
          .arcade-arrow--spinning {
            animation: arrowMechanicalTick 0.08s ease-in-out infinite alternate !important;
          }
          .arcade-winning-slice-pulse {
            animation: arcadeWinningSlicePulse 1.2s ease-in-out infinite alternate;
          }
          .arcade-spin-action {
            transition: transform 0.15s ease, filter 0.2s ease;
          }
          .arcade-spin-action:hover:not(:disabled) {
            filter: brightness(1.18) drop-shadow(0 0 22px rgba(255, 170, 40, 0.95)) !important;
            transform: translate(-50%, -50%) scale(1.06) !important;
          }
          .arcade-spin-action:active:not(:disabled) {
            transform: translate(-50%, -50%) scale(0.93) !important;
            filter: brightness(0.95) !important;
          }
          .arcade-spin-action:disabled {
            filter: grayscale(0.25) brightness(0.85) drop-shadow(0 6px 16px rgba(0,0,0,0.85)) !important;
          }
          .arcade-close-btn {
            transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.15s ease;
          }
          .arcade-close-btn:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.16) !important;
            box-shadow: 0 0 18px rgba(252, 68, 32, 0.75);
            transform: scale(1.08);
          }
          @media (prefers-reduced-motion: reduce) {
            .trade-wheel-arcade__entrance,
            .arcade-laser-beam,
            .arcade-arrow--spinning,
            .arcade-winning-slice-pulse {
              animation: none !important;
            }
          }
        `}</style>

        <div
          ref={cardRef}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={config.title}
          style={{
            position: "relative",
            width: "min(92vw, 420px, calc((100dvh - 24px) * 0.714))",
            userSelect: "none",
          }}
        >
          <div
            className="trade-wheel-arcade__entrance"
            style={{
              position: "relative",
              width: "100%",
              animation: "arcadeWheelEntrance 0.35s cubic-bezier(0.16, 1, 0.3, 1) both",
              filter: "drop-shadow(0 20px 50px rgba(0,0,0,0.95)) drop-shadow(0 0 35px rgba(252, 68, 32, 0.45))",
            }}
          >
            {/* Rotating disc, sized to the chassis window */}
            <div
              ref={wheelRef}
              style={{
                position: "absolute",
                left: "9.5%",
                top: "27.51%",
                width: "81%",
                aspectRatio: "1 / 1",
                borderRadius: "50%",
                background: wheelBackground,
                overflow: "hidden",
                zIndex: 1,
                willChange: "transform",
                boxShadow: "inset 0 0 25px rgba(0, 0, 0, 0.65)",
              }}
            >
              {/* Winning slice radiant spotlight overlay */}
              {winningIndex !== null && !isSpinning && (
                <svg
                  viewBox="-200 -200 400 400"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    zIndex: 4,
                  }}
                >
                  <defs>
                    <radialGradient id="arcadeWinnerSliceGlow" cx="0%" cy="-60%" r="95%">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
                      <stop offset="35%" stopColor="#ffd700" stopOpacity="0.8" />
                      <stop offset="70%" stopColor="#ff9900" stopOpacity="0.45" />
                      <stop offset="100%" stopColor="#ff5500" stopOpacity="0.15" />
                    </radialGradient>
                    <filter id="arcadeGoldBloom" x="-30%" y="-30%" width="160%" height="160%">
                      <feGaussianBlur stdDeviation="4" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <path
                    d={getSectorPath(winningIndex * segmentAngle, (winningIndex + 1) * segmentAngle, 198)}
                    fill="url(#arcadeWinnerSliceGlow)"
                    stroke="#ffffff"
                    strokeWidth="2.5"
                    filter="url(#arcadeGoldBloom)"
                    className="arcade-winning-slice-pulse"
                  />
                  <path
                    d={getSectorPath(winningIndex * segmentAngle, (winningIndex + 1) * segmentAngle, 198)}
                    fill="none"
                    stroke="#ffd700"
                    strokeWidth="3.5"
                    strokeDasharray="4 2"
                  />
                </svg>
              )}

              {segments.map((segment, index) => {
                const angle = index * segmentAngle + segmentAngle / 2;
                const safeLabel =
                  typeof segment.label === "string" && segment.label.trim().length > 0
                    ? segment.label
                    : `Slice ${index + 1}`;
                const { valueText, subText } = parseWheelLabel(safeLabel);
                const isWinner = winningIndex === index && !isSpinning;
                const icon = getRewardIcon(safeLabel, segment?.rewardType);

                return (
                  <div
                    key={`${segment.label}-${index}`}
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: 0,
                      width: "48px",
                      marginLeft: "-24px",
                      height: "50%",
                      transformOrigin: "50% 100%",
                      transform: `rotate(${angle}deg)`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      paddingTop: isWinner ? "4%" : "5.5%",
                      pointerEvents: "none",
                      zIndex: isWinner ? 8 : 2,
                      transition: "all 0.3s ease",
                    }}
                  >
                    <div
                      className={isWinner ? "arcade-winning-spoke-badge" : "arcade-spoke-content"}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        width: "100%",
                        padding: isWinner ? "4px 2px" : "2px 1px",
                        borderRadius: "8px",
                        background: isWinner
                          ? "radial-gradient(ellipse at center, rgba(0, 0, 0, 0.78) 0%, rgba(20, 10, 0, 0.65) 100%)"
                          : "rgba(0, 0, 0, 0.22)",
                        border: isWinner ? "1.5px solid #ffd700" : "1px solid transparent",
                        boxShadow: isWinner
                          ? "0 0 16px rgba(255, 215, 0, 0.9), inset 0 0 6px rgba(255, 215, 0, 0.5)"
                          : "none",
                        transform: isWinner ? "scale(1.15)" : "scale(1)",
                        transformOrigin: "center 20%",
                        transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: isWinner ? "clamp(12px, 3.4vw, 15px)" : "clamp(10px, 2.8vw, 12px)",
                          lineHeight: 1,
                          marginBottom: "1px",
                          filter: isWinner
                            ? "drop-shadow(0 0 8px #ffd700)"
                            : "drop-shadow(0 1px 3px rgba(0,0,0,0.9))",
                        }}
                      >
                        {icon}
                      </span>
                      <span
                        style={{
                          color: "#ffffff",
                          fontSize: isWinner ? "clamp(10px, 3vw, 12.5px)" : "clamp(9px, 2.6vw, 11px)",
                          fontWeight: 900,
                          lineHeight: 1.05,
                          letterSpacing: "0.02em",
                          whiteSpace: "nowrap",
                          textShadow: isWinner
                            ? "0 0 8px #ffffff, 0 0 16px #ffd700, 0 0 25px #ff8800"
                            : "0 0 3px #000, 1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 0 2px 4px rgba(0,0,0,0.9)",
                        }}
                      >
                        {valueText}
                      </span>
                      {subText ? (
                        <span
                          style={{
                            color: isWinner ? "#ffd700" : "#ffffff",
                            fontSize: isWinner ? "clamp(8px, 2.4vw, 10px)" : "clamp(7.5px, 2.1vw, 9px)",
                            fontWeight: 800,
                            lineHeight: 1,
                            letterSpacing: subText === "Multiplier" ? "-0.2px" : "0.02em",
                            whiteSpace: "nowrap",
                            marginTop: "1px",
                            textShadow: isWinner
                              ? "0 0 8px #ffd700, 0 0 14px #ff8800"
                              : "0 0 3px #000, 1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000",
                          }}
                        >
                          {subText}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Stationary arcade chassis: crown, gold ring, bulbs and ruby pointer. */}
            <img
              src={spinWheelChassisImg}
              alt=""
              draggable={false}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                position: "relative",
                zIndex: 2,
                pointerEvents: "none",
              }}
            />

            {/* Hit area over the X printed on the chassis */}
            <button
              type="button"
              className="arcade-close-btn"
              onClick={onClose}
              disabled={isBusy}
              data-no-tilt-press
              title="Close Spin Wheel"
              aria-label="Close"
              style={{
                position: "absolute",
                left: "89.4%",
                top: "7.8%",
                width: "10.6%",
                aspectRatio: "1 / 1",
                borderRadius: "50%",
                background: "transparent",
                border: "none",
                cursor: isBusy ? "not-allowed" : "pointer",
                padding: 0,
                outline: "none",
                zIndex: 15,
              }}
            />

            {/* Live Remaining Spin pill */}
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: "18.5%",
                transform: "translateX(-50%)",
                width: "50%",
                height: "6.1%",
                borderRadius: "999px",
                background: "#1a0603",
                border: "2px solid #fc4420",
                boxShadow: "0 0 16px rgba(252, 68, 32, 0.55), inset 0 1px 4px rgba(0,0,0,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ color: "#ffffff", fontSize: "clamp(10.5px, 3.1vw, 13px)", fontWeight: 800, letterSpacing: "0.4px" }}>
                Remaining Spin:
                <strong style={{ color: "#ffd64e", fontWeight: 900, marginLeft: "5px" }}>
                  {remainingSpins}
                </strong>
              </span>
            </div>

            {/* 3D centre SPIN button */}
            <button
              type="button"
              className="arcade-spin-action"
              onClick={handleSpin}
              disabled={spinDisabled}
              data-no-tilt-press
              title={isSpinning ? "Wheel is spinning..." : "Click to Spin!"}
              aria-label={isSpinning ? "Spinning" : "Spin Wheel"}
              aria-busy={isBusy}
              style={{
                position: "absolute",
                left: "50%",
                top: "56.5%",
                width: "26.5%",
                aspectRatio: "1 / 1",
                transform: "translate(-50%, -50%)",
                borderRadius: "50%",
                background: "transparent",
                border: "none",
                cursor: spinDisabled ? "not-allowed" : "pointer",
                padding: 0,
                outline: "none",
                zIndex: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.85)) drop-shadow(0 0 18px rgba(245, 158, 11, 0.55))",
              }}
            >
              <img
                src={spinWheelCenterBtnImg}
                alt=""
                draggable={false}
                style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
              />
            </button>

            {/* Active 3D Ruby Arrow Indicator at 12 o'clock pointing down */}
            <div
              className={`arcade-arrow-indicator ${isSpinning ? "arcade-arrow--spinning" : ""} ${winningIndex !== null && !isSpinning ? "arcade-arrow--winner" : ""}`}
              style={{
                position: "absolute",
                left: "50%",
                top: "24.2%",
                transform: "translateX(-50%)",
                width: "36px",
                height: "46px",
                zIndex: 14,
                pointerEvents: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* SVG 3D Ruby Needle with Gold Bezel */}
              <svg
                viewBox="0 0 36 46"
                style={{
                  width: "100%",
                  height: "100%",
                  filter: winningIndex !== null && !isSpinning
                    ? "drop-shadow(0 0 10px #ffd700) drop-shadow(0 0 20px #ff1744)"
                    : "drop-shadow(0 2px 6px rgba(0,0,0,0.85)) drop-shadow(0 0 8px rgba(255, 30, 0, 0.6))",
                  transition: "filter 0.3s ease",
                }}
              >
                <defs>
                  <linearGradient id="needleGoldRim" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fff3a8" />
                    <stop offset="40%" stopColor="#ffd700" />
                    <stop offset="70%" stopColor="#c88a00" />
                    <stop offset="100%" stopColor="#684200" />
                  </linearGradient>
                  <linearGradient id="needleRubyGem" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#ff5252" />
                    <stop offset="45%" stopColor="#d50000" />
                    <stop offset="100%" stopColor="#8a0000" />
                  </linearGradient>
                  <linearGradient id="needleGemFacet" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.7)" />
                    <stop offset="50%" stopColor="rgba(255,255,255,0)" />
                    <stop offset="100%" stopColor="rgba(0,0,0,0.4)" />
                  </linearGradient>
                </defs>
                {/* Gold Bezel Outline */}
                <path
                  d="M 5 4 L 31 4 L 31 24 L 18 42 L 5 24 Z"
                  fill="url(#needleGoldRim)"
                  stroke="#ffffff"
                  strokeWidth="0.8"
                />
                {/* Ruby Gem Core */}
                <path
                  d="M 8 7 L 28 7 L 28 22 L 18 37 L 8 22 Z"
                  fill="url(#needleRubyGem)"
                />
                <path
                  d="M 8 7 L 18 7 L 18 37 L 8 22 Z"
                  fill="url(#needleGemFacet)"
                />
                <polygon
                  points="18,10 23,20 18,34 13,20"
                  fill="rgba(255,255,255,0.3)"
                />
                {/* Center glowing tip star */}
                <circle cx="18" cy="38" r="2" fill="#ffffff" filter="drop-shadow(0 0 3px #fff)" />
              </svg>

              {/* Laser Alignment Beam shooting down directly into the winning slice */}
              {winningIndex !== null && !isSpinning && (
                <div
                  className="arcade-laser-beam"
                  style={{
                    position: "absolute",
                    top: "38px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "3px",
                    height: "72px",
                    background: "linear-gradient(to bottom, #ffffff 0%, #ffd700 35%, rgba(255, 215, 0, 0.4) 75%, transparent 100%)",
                    boxShadow: "0 0 10px #ffd700, 0 0 20px #ff9900",
                    borderRadius: "2px",
                    zIndex: 15,
                  }}
                />
              )}
            </div>

            {/* Winner Floating Celebration Banner */}
            {showWinCelebration && winningIndex !== null && !isSpinning && (
              <div
                className="arcade-win-banner"
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "72.8%",
                  transform: "translateX(-50%)",
                  zIndex: 25,
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                  background: "linear-gradient(135deg, rgba(20, 10, 2, 0.95) 0%, rgba(45, 22, 5, 0.95) 100%)",
                  border: "2px solid #ffd700",
                  borderRadius: "999px",
                  padding: "5px 16px",
                  boxShadow: "0 0 25px rgba(255, 215, 0, 0.8), 0 8px 24px rgba(0,0,0,0.9)",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  animation: "arcadeWinBannerPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both",
                }}
              >
                <span style={{ fontSize: "15px" }}>🎉</span>
                <span style={{ color: "#ffd700", fontWeight: 900, fontSize: "clamp(11px, 3.2vw, 13px)", letterSpacing: "0.5px" }}>
                  WON: {resultLabel}!
                </span>
                <span style={{ fontSize: "15px" }}>✨</span>
              </div>
            )}

            {/* Covers the printed result pill with the live result */}
            <div
              aria-live="polite"
              style={{
                position: "absolute",
                left: "50%",
                top: "88.4%",
                transform: "translateX(-50%)",
                width: "56.5%",
                height: "8.6%",
                borderRadius: "999px",
                background: winningIndex !== null && !isSpinning
                  ? "linear-gradient(180deg, #2a1604 0%, #150802 100%)"
                  : "#120402",
                border: winningIndex !== null && !isSpinning
                  ? "2px solid #ffd700"
                  : "1.5px solid rgba(252, 68, 32, 0.6)",
                boxShadow: winningIndex !== null && !isSpinning
                  ? "0 0 25px rgba(255, 215, 0, 0.75), inset 0 1px 6px rgba(255, 215, 0, 0.4)"
                  : "0 0 14px rgba(0, 0, 0, 0.7), inset 0 1px 4px rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                color: "#ffffff",
                fontWeight: 800,
                fontSize: "clamp(11px, 3.2vw, 13.5px)",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                transition: "all 0.35s ease",
              }}
            >
              <span>Result:&nbsp;</span>
              <span
                style={{
                  color: isSpinning ? "#ffd64e" : (winningIndex !== null ? "#ffd700" : "#ffd64e"),
                  fontWeight: 900,
                  textShadow: winningIndex !== null && !isSpinning
                    ? "0 0 10px #ffd700, 0 0 20px #ff9900"
                    : "none",
                }}
              >
                {isSpinning ? "Spinning..." : (resultLabel ? `🏆 ${resultLabel}` : "--")}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== ORIGINAL JACKPOT SPIN WHEEL (Unchanged) =====
  return (
    <div className="trade-wheel-modal" role="presentation">
      {(isSpinning || isResultProcessing) && (
        <div className="absolute top-[20px] z-[99999] -translate-x-1/2">
          <div className="w-[500px] overflow-hidden rounded-full border max-w-[320px] border-orange-500/20 bg-[#101010]/95 py-2 shadow-[0_0_25px_rgba(255,120,0,0.25),0_0_60px_rgba(255,120,0,0.35)]">
            <div className="whitespace-nowrap animate-[marquee_7s_linear_infinite] text-sm font-medium text-orange-300">
              Warning: Leaving or reloading now may cause you to lose your reward.
            </div>
          </div>
        </div>
      )}
      <div
        className={`trade-wheel-modal__panel trade-wheel-modal__panel--jackpot`}
        role="dialog"
        aria-modal="true"
        aria-label={config.title}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="trade-wheel-modal__close"
          onClick={onClose}
          aria-label="Close wheel modal"
          disabled={isSpinning || isResultProcessing}
          style={{
            cursor: isSpinning || isResultProcessing ? "not-allowed" : "pointer",
            opacity: isSpinning || isResultProcessing ? 0.5 : 1,
          }}
        >
          x
        </button>
        <div className="trade-wheel-modal__jackpot-bg" aria-hidden="true">
          <header className="trade-wheel-modal__jackpot-header">
            <h2>JACKPOT</h2>
            <h2> SPIN</h2>
          </header>
          <p>Jackpot Pool:</p>
          <strong>${Number(poolAmount || 0).toLocaleString()}</strong>
        </div>
        <div className="trade-wheel-modal__wheel-wrap">
          <span
            className="trade-wheel-modal__pointer trade-wheel-modal__pointer--jackpot"
            aria-hidden="true"
          />
          <div
            ref={wheelRef}
            className="trade-wheel-modal__wheel"
            style={{ background: wheelBackground }}
          >
            {segments.map((segment, index) => {
              const angle = index * segmentAngle + segmentAngle / 2;
              const angleInRadians = ((angle - 90) * Math.PI) / 180;
              const safeLabel =
                typeof segment.label === "string" && segment.label.trim().length > 0
                  ? segment.label
                  : `Slice ${index + 1}`;
              const labelLines = formatWheelLabel(safeLabel);
              const x = 50 + Math.cos(angleInRadians) * labelRadiusPercent;
              const y = 50 + Math.sin(angleInRadians) * labelRadiusPercent;
              let textRotation = angle - 90;
              if (angle > 180) textRotation += 180;
              const transformStr = `translate(-50%, -50%) rotate(${textRotation}deg)`;

              return (
                <span
                  key={`${segment.label}-${index}`}
                  className="trade-wheel-modal__segment-label trade-wheel-modal__segment-label--jackpot"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: transformStr,
                    transformOrigin: "50% 50%",
                    color: index % 2 === 0 ? "#fef08a" : "#450a0a",
                    textShadow: index % 2 !== 0 ? "none" : "1px 1px 1px rgba(0,0,0,0.8), -1px -1px 1px rgba(0,0,0,0.8), 1px -1px 1px rgba(0,0,0,0.8), -1px 1px 1px rgba(0,0,0,0.8)",
                    width: "auto",
                    textAlign: "center",
                    fontWeight: 800,
                  }}
                >
                  {labelLines.map((line, lineIndex) => (
                    <span
                      key={`${safeLabel}-${lineIndex}`}
                      className="trade-wheel-modal__segment-label-line"
                    >
                      {line}
                    </span>
                  ))}
                </span>
              );
            })}
          </div>
          <div className="trade-wheel-modal__wheel-shine" aria-hidden="true" />
        </div>
        <div className="trade-wheel-modal__actions trade-wheel-modal__actions--jackpot">
          <button
            type="button"
            className="trade-wheel-modal__spin-btn trade-wheel-modal__spin-btn--jackpot"
            onClick={() => {
              playSound("button");
              handleSpin();
            }}
            disabled={isSpinning || isResultProcessing || !canSpinAgain}
            aria-busy={isSpinning || isResultProcessing}
          >
            {isSpinning ? "Spinning..." : "Spin Jackpot"}
          </button>
          <p
            className={`trade-wheel-modal__result ${!resultLabel && !isSpinning ? "trade-wheel-modal__result--empty" : ""}`}
            aria-live="polite"
          >
            Result:{" "}
            {isSpinning ? (
              "Spinning..."
            ) : (
              <RewardValueBadge
                value={resultLabel || "--"}
                className="trade-wheel-modal__result-badge"
              />
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
